import { useConfiguratorStateDomain } from "@/configurator/domain/hooks/useConfiguratorStateDomain";
import {
  findActiveModelOption,
  findProduct,
} from "@/configurator/domain/logic/catalog";
import {
  resolveChrome,
  resolveFontFamily,
} from "@/configurator/domain/logic/presentation";
import type {
  BrandConfig,
  ConfiguratorConfig,
  ModelOption,
  RendererKind,
} from "@/configurator/domain/models/types";
import type { ConfiguratorSnapshot } from "@/configurator/domain/ports/configuratorState";

/*
 * Selectors live at module scope so their identity is stable: the state port
 * caches a selector's result per snapshot, and an inline arrow would defeat
 * that and hand every consumer a fresh object on each render — which is how an
 * effect keyed on one of these starts firing every render.
 */
const selectConfig = (s: ConfiguratorSnapshot) => s.config;

const selectActiveProduct = (s: ConfiguratorSnapshot) =>
  findProduct(s.config.catalog, s.activeProductId);

const selectActiveModel = (s: ConfiguratorSnapshot) =>
  findActiveModelOption(s.config, s.selectedModelId);

export type ConfiguratorDomain = {
  /** Product title; absent until a product resolves out of the catalog. */
  title: string | undefined;
  /** Slug-ish id used to name downloads; absent when no product resolved. */
  productId: string | undefined;
  renderer: RendererKind;
  hasChrome: boolean;
  fontFamily: string;
  brand: BrandConfig;
  activeModel: ModelOption | undefined;
  applyConfig: (config: ConfiguratorConfig) => void;
  downloadModel: () => Promise<void>;
};

/**
 * The configurator screen as a whole: what it is called, how it is framed, and
 * the two writes that belong to the shell rather than to a panel.
 */
export function useConfiguratorDomain(): ConfiguratorDomain {
  const { useSelect, commands } = useConfiguratorStateDomain();
  const config = useSelect(selectConfig);
  const product = useSelect(selectActiveProduct);
  const activeModel = useSelect(selectActiveModel);

  return {
    title: product?.title,
    productId: product?.id,
    renderer: config.defaultRenderer,
    hasChrome: resolveChrome(config),
    fontFamily: resolveFontFamily(config),
    brand: config.brand,
    activeModel,
    applyConfig: commands.setConfig,
    downloadModel: commands.downloadActiveModel,
  };
}
